import React, {useEffect, useState} from "react"
import {useLocation, useNavigate} from "react-router-dom"
// MUI
import {Box, Button, Collapse, Stack, Typography} from "@mui/material";
import {Check} from "@mui/icons-material";
// Components
import {DismountConfirmationDialog} from "@/components/UI";
import {ProductsEditor} from "@/components/Products";
import {BuilderShell} from "@/components/Layout";
import {packagingTokens as tk} from "@/theme/packagingTokens";
// Translation
import {useTranslation} from "react-i18next";
// Data
import {useData} from "@/context/PackagingDataProvider";

export default function ProductBuilder(props: any) {

    const {state} = useLocation();
    const redirect = useNavigate()

    const {creating, editing, gs1} = props
    const {createAny, editAny, brands, ranges, getBrands, getRanges, getSingleProduct} = useData()
    const {t} = useTranslation(["products", "common"])

    const savedProduct = localStorage.getItem("packaging-savedProduct")

    const initProduct = {
        brand: "", range: "", id: "", name: "", qrcode: "", bypass_app: true,
        sku: "", default_url: "", type: gs1 ? "GS1" : "Standard"
    }

    const initGS1Product = {
        primaryKey: "01",
        primaryKeyValue: "",
        key_identifiers: [
            {key: "22", value: ""},
            {key: "10", value: ""},
            {key: "21", value: ""},
        ],
        dataAttributes: [{key: "", value: ""}]
    }

    // Dismount confirmation
    const [openDismountDialog, setOpenDismountDialog] = useState<string>("")
    // Data
    const [product, setProduct] = useState<any>(initProduct)
    const [GS1Product, setGS1Product] = useState<any>(initGS1Product)
    // Recovery of an unfinished creation (QR Code already created, product
    // never finalised): `recovery` holds the parsed localStorage payload
    // while the banner offers to resume it; `recovered` is set once it has
    // been restored into the form. For GS1, `recoveredHasGS1Data` locks the
    // restored GS1 fields (the QR Code already encodes their short URL) —
    // legacy payloads saved without the GS1 form data stay editable instead.
    const [recovery, setRecovery] = useState<any>(null)
    const [recovered, setRecovered] = useState<boolean>(false)
    const [recoveredHasGS1Data, setRecoveredHasGS1Data] = useState<boolean>(false)
    // In-flight guard: product creation is not idempotent, so a double click
    // would create a second product against the same QR code.
    const [submitting, setSubmitting] = useState<boolean>(false)

    // Disabling navigation
    const blocking = Boolean(!editing && !!product.qrcode)

    // Keyed on the location state: the wrong-flavour redirect in
    // resumeRecovery targets the same component (no remount), so a
    // mount-only effect would miss its `autoResume` marker.
    useEffect(() => {
        // Recovery only concerns creation — never surface it in the editor.
        if (!editing && !recovered) recoverProductCreation()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    useEffect(() => {
        if (!brands) getBrands()
        if (!ranges) getRanges()

        if (editing) {
            getSingleProduct(state.id).then((res: any) => {
                setProduct({
                    range: res.range.id,
                    id: res.id,
                    name: res.name,
                    qrcode: res.qr_code.id,
                    bypass_app: res.bypass_app,
                    sku: res.sku,
                    gtin: !!res.gtin ? res.gtin : "",
                    default_url: res.default_url,
                    type: gs1 ? "GS1" : "Standard",
                    attributed_user_uuid: res.attributed_user_uuid ?? "",
                    attributed_user_email: res.attributed_user_email ?? "",
                    attributed_org_uuid: res.attributed_org_uuid ?? "",
                    attributed_org_name: res.attributed_org_name ?? "",
                })
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props, state])

    // GS1 - clearing key qualifiers on primary key change
    useEffect(() => {
        // Skipped while a recovery is pending or restored: the change then
        // comes from restoring the saved payload, not from the user.
        if (!recovery && !recovered) setGS1Product({...GS1Product, key_identifiers: []})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [GS1Product?.primaryKey])

    // Product saving — GS1 form data is saved along so a recovery can
    // restore it (the Look Up then has to be run again on the same data).
    useEffect(() => {
        if (!editing && !!product.qrcode) saveProductCreation()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [product, GS1Product]);

    const saveProductCreation = () => {
        let _product = gs1 ? {product: product, GS1Product: GS1Product} : {product: product}
        localStorage.setItem("packaging-savedProduct", JSON.stringify(_product))
    }

    const recoverProductCreation = () => {
        if (!savedProduct) return
        let _savedProduct: any = null
        try { _savedProduct = JSON.parse(savedProduct) } catch (e) { }
        // Malformed or empty leftover (no QR Code was created) — nothing
        // worth recovering, and offering it would only lead to errors.
        if (!_savedProduct?.product?.qrcode) {
            localStorage.removeItem("packaging-savedProduct")
            return
        }
        // Arriving from the wrong-flavour redirect below: restore right away.
        if (state?.autoResume) resumeRecovery(_savedProduct)
        else setRecovery(_savedProduct)
    }

    const resumeRecovery = (payload: any) => {
        const savedIsGS1 = payload.product.type === "GS1"
        // The saved creation belongs to the other builder flavour — reopen it
        // there; recovery is picked up again (and auto-resumed) on arrival.
        if (savedIsGS1 !== !!gs1) {
            redirect(savedIsGS1 ? "/products/gs1/create" : "/products/create", {state: {autoResume: true}})
            return
        }
        setProduct({...initProduct, ...payload.product})
        if (savedIsGS1 && !!payload.GS1Product) {
            setGS1Product(payload.GS1Product)
            setRecoveredHasGS1Data(true)
        }
        setRecovery(null)
        setRecovered(true)
    }

    const deleteRecovery = () => {
        localStorage.removeItem("packaging-savedProduct")
        setProduct(initProduct)
        setGS1Product(initGS1Product)
        setRecovery(null)
        setRecovered(false)
        setRecoveredHasGS1Data(false)
    }

    const handleCreateProduct = (passedProduct: any) => {
        if (submitting) return
        setSubmitting(true)
        createAny(passedProduct, "product").then((created: any) => {
            if (created) {
                localStorage.removeItem("packaging-savedProduct")
                redirect("/?tab=products")
            } else {
                // Only re-enable on failure; on success we navigate away and
                // re-enabling would briefly re-arm the button mid-redirect.
                setSubmitting(false)
            }
        }).catch(() => setSubmitting(false))
    }

    const handleEditProduct = () => {
        if (submitting) return
        setSubmitting(true)
        editAny(product, "product").then((updated: any) => {
            if (updated) redirect("/?tab=products")
            else setSubmitting(false)
        }).catch(() => setSubmitting(false))
    }

    const handleQuit = () => {
        if (blocking) setOpenDismountDialog("/?tab=products")
        else redirect("/?tab=products")
    }

    const onNavigateOut = () => {
        let href = openDismountDialog
        setOpenDismountDialog("")
        if (String(href).startsWith("http")) {
            let a = document.createElement('a')
            a.href = href
            a.target = "_parent"
            a.click()
        } else redirect(href)
    }

    const title =
        (editing && gs1) ? t("gs1_product_editor")
        : (!editing && gs1) ? t("gs1_product_creation")
        : (editing && !gs1) ? t("product_editor")
        : t("product_creation")

    // Recovery assistant — a token info card (was a pale-blue paper).
    // "Resume creation" restores the saved payload into the form so the
    // creation finishes through the regular, validated flow (no blind
    // creation of a possibly incomplete product).
    const renderRecoveryAssistant = () => (
        <Collapse in={!!recovery}>
            <Box
                sx={{
                    mb: 2.5, p: 2,
                    borderRadius: tk.radius.card,
                    backgroundColor: tk.color.blue100,
                    border: `1px solid ${tk.color.blue}33`,
                }}
            >
                <Typography sx={{fontSize: "13.5px", fontWeight: 700, mb: 1.5}}>
                    {t("recovery_alert")}
                </Typography>
                <Stack direction={"row"} spacing={1} justifyContent={"flex-end"} flexWrap={"wrap"}>
                    <Button
                        onClick={() => deleteRecovery()}
                        sx={{textTransform: "none", fontWeight: 600, borderRadius: tk.radius.sm, color: tk.color.red, border: `1px solid ${tk.color.red}55`}}
                    >
                        {t("start_anew")}
                    </Button>
                    <Button
                        onClick={() => resumeRecovery(recovery)}
                        variant={"contained"} disableElevation startIcon={<Check />}
                        sx={{textTransform: "none", fontWeight: 600, borderRadius: tk.radius.sm}}
                    >
                        {t("resume_creation")}
                    </Button>
                </Stack>
            </Box>
        </Collapse>
    )

    return (
        <>
            <BuilderShell
                onBack={handleQuit}
                backLabel={`${t("common:back")}`}
                title={title}
                subtitle={editing ? product.name : t("common:products")}
                maxWidth={860}
            >
                {renderRecoveryAssistant()}

                <ProductsEditor
                    onCreate={() => handleCreateProduct(product)}
                    onEdit={() => handleEditProduct()}
                    recoveredGS1={gs1 && recovered}
                    recoveredGS1Locked={gs1 && recovered && recoveredHasGS1Data}
                    {...{creating, editing, GS1Product, setGS1Product, product, setProduct, brands, ranges, gs1, submitting, t}}
                />
            </BuilderShell>

            <DismountConfirmationDialog
                onDismount={() => onNavigateOut()}
                {...{openDismountDialog, setOpenDismountDialog, t}}
            />
        </>
    )
}
